import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { listPrixOuvrage, updatePrixOuvrage, createPrixOuvrage, deletePrixOuvrage } from "../services/api";
import "./PrixOuvrageForm.css";

const defaultCharges = [
  "Frais financiers",
  "Emprunt", 
  "Frais comptable",
  "Loyer",
  "Frais généraux",
  "Charge sociale"
];

const PrixOuvrageForm = () => {
  const navigate = useNavigate();
  
  const [charges, setCharges] = useState([]);
  const [customLabel, setCustomLabel] = useState("");
  const [customBudget, setCustomBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [distributingCharges, setDistributingCharges] = useState(false);
  const [distributionMessage, setDistributionMessage] = useState(null);

  useEffect(() => {
    loadCharges();
  }, []);

  const loadCharges = async () => {
    try {
      const data = await listPrixOuvrage();
      // Ensure all default charges exist
      const existingNames = data.map(item => item.nom);
      const missingCharges = defaultCharges.filter(charge => !existingNames.includes(charge));
      
      // Create missing default charges
      for (const chargeName of missingCharges) {
        await createPrixOuvrage({ nom: chargeName, prix_mois: 0 });
      }
      
      // Reload data
      const updatedData = await listPrixOuvrage();
      setCharges(updatedData);
    } catch (err) {
      setError("Erreur de chargement");
    }
  };

  const handleChargeChange = (id, value) => {
    setCharges(charges.map(charge => 
      charge.id === id ? { ...charge, prix_mois: Number(value) } : charge
    ));
    setError("");
  };

  const addCustomCharge = async () => {
    if (!customLabel || !customBudget) return;
    try {
      const newCharge = await createPrixOuvrage({
        nom: customLabel,
        prix_mois: Number(customBudget)
      });
      setCharges([...charges, newCharge]);
      setCustomLabel("");
      setCustomBudget("");
    } catch (err) {
      setError("Erreur lors de l'ajout de la charge");
    }
  };

  const removeCustomCharge = async (id) => {
    try {
      await deletePrixOuvrage(id);
      setCharges(charges.filter(charge => charge.id !== id));
    } catch (err) {
      setError("Erreur lors de la suppression");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Update all charges
      for (const charge of charges) {
        await updatePrixOuvrage(charge.id, {
          nom: charge.nom,
          prix_mois: Number(charge.prix_mois)
        });
      }
      navigate("/prix-ouvrage");
    } catch (err) {
      setError("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const triggerDailyDistribution = async (force = false) => {
    if (distributingCharges) return;
    
    try {
      setDistributingCharges(true);
      setDistributionMessage(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/prix-ouvrage/distribute-daily-charges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDistributionMessage({
          type: 'success',
          text: `Distribution réussie! ${data.details?.message || 'Charges distribuées aux chantiers actifs.'}`
        });
        
        // Refresh the charges list to show updated values
        await loadCharges();
      } else {
        setDistributionMessage({
          type: 'warning',
          text: `${data.message || data.details?.message || 'Aucune charge distribuée.'}`
        });
      }
    } catch (err) {
      setDistributionMessage({
        type: 'error',
        text: `Erreur réseau: ${err.message}`
      });
    } finally {
      setDistributingCharges(false);
    }
  };

  const isCustomCharge = (chargeName) => !defaultCharges.includes(chargeName);

  const calculateTotal = () => {
    return charges.reduce((total, charge) => total + Number(charge.prix_mois || 0), 0);
  };

  return (
    <div className="prix-ouvrage-container">
      <form className="prix-ouvrage-form" onSubmit={handleSubmit}>
        <h2 className="prix-ouvrage-title">Configuration Charges fixes</h2>
        <p className="prix-ouvrage-subtitle">
          Configurez les charges mensuelles pour le calcul du prix d'ouvrage
        </p>

        {calculateTotal() > 0 && (
          <div className="total-display">
            <div className="total-amount">{calculateTotal().toFixed(2)} €</div>
            <div className="total-label">Total mensuel</div>
          </div>
        )}

        {/* Admin actions */}
        <div className="admin-actions" style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            type="button"
            onClick={() => triggerDailyDistribution(false)}
            disabled={distributingCharges || loading}
            className="action-button"
            style={{ backgroundColor: '#6772e5', marginLeft: 'auto' }}
          >
            {distributingCharges ? 'Distribution en cours...' : 'Distribuer charges quotidiennes'}
          </button>
          <button
            type="button"
            onClick={() => triggerDailyDistribution(true)}
            disabled={distributingCharges || loading}
            className="action-button"
            style={{ backgroundColor: '#e67e22' }}
            title="Forcer la distribution même si déjà exécutée aujourd'hui"
          >
            Forcer
          </button>
        </div>

        {distributionMessage && (
          <div className={`message ${distributionMessage.type}`} style={{
            padding: '10px 15px',
            borderRadius: '4px',
            marginBottom: '20px',
            backgroundColor: distributionMessage.type === 'success' ? '#e8f5e9' : '#ffebee',
            color: distributionMessage.type === 'success' ? '#2e7d32' : '#c62828',
            border: `1px solid ${distributionMessage.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`
          }}>
            {distributionMessage.text}
          </div>
        )}

        <div className="form-section">
          <h3 className="section-title">
            <span className="section-icon">💼</span>
            Charges fixes
          </h3>
          <div className="charges-grid">
            {charges.filter(charge => !isCustomCharge(charge.nom)).map((charge) => (
              <div key={charge.id} className="charge-input-group">
                <label className="charge-label">{charge.nom}</label>
                <input
                  type="number"
                  value={charge.prix_mois}
                  onChange={(e) => handleChargeChange(charge.id, e.target.value)}
                  required
                  min="0"
                  step="0.01"
                  className="charge-input"
                  placeholder="0.00"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="add-charge-section">
            <h3 className="add-charge-title">
              <span className="section-icon">➕</span>
              Ajouter une charge personnalisée
            </h3>
            <div className="add-charge-form">
              <div className="add-charge-input">
                <label className="charge-label">Nom de la charge</label>
                <input
                  type="text"
                  placeholder="Ex: Assurance, Maintenance..."
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  className="charge-input"
                />
              </div>
              <div className="add-charge-input">
                <label className="charge-label">Montant mensuel (€)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={customBudget}
                  onChange={(e) => setCustomBudget(e.target.value)}
                  className="charge-input"
                  min="0"
                  step="0.01"
                />
              </div>
              <button
                type="button"
                className="add-charge-button"
                onClick={addCustomCharge}
                disabled={!customLabel || !customBudget}
              >
                <span>➕</span>
                Ajouter
              </button>
            </div>
          </div>
        </div>

        {charges.filter(charge => isCustomCharge(charge.nom)).length > 0 && (
          <div className="form-section">
            <div className="custom-charges-list">
              <h3 className="custom-charges-title">
                <span className="section-icon">📋</span>
                Charges personnalisées
              </h3>
              {charges.filter(charge => isCustomCharge(charge.nom)).map((charge) => (
                <div key={charge.id} className="custom-charge-item">
                  <div className="custom-charge-info">
                    <span className="custom-charge-name">{charge.nom}</span>
                    <input
                      type="number"
                      value={charge.prix_mois}
                      onChange={(e) => handleChargeChange(charge.id, e.target.value)}
                      min="0"
                      step="0.01"
                      className="custom-charge-input"
                    />
                    <span>€/mois</span>
                  </div>
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => removeCustomCharge(charge.id)}
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <span>⚠️</span>
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="action-button cancel-button"
            onClick={() => navigate("/prix-ouvrage")}
            disabled={loading}
          >
            Annuler
          </button>
          <button 
            type="submit" 
            className="action-button save-button" 
            disabled={loading}
          >
            {loading && <span className="loading-spinner"></span>}
            {loading ? "Sauvegarde..." : "Sauvegarder"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PrixOuvrageForm;
